import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Spin, message } from 'antd';
import { TrophyOutlined, TrophyTwoTone, RiseOutlined } from '@ant-design/icons';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import dayjs from 'dayjs';

const { Title } = Typography;

interface TournamentResult {
  tournamentId: number;
  tournamentName: string;
  tournamentDate: string;
  position: number | null;
  categoryName: string | null;
}

interface StatisticsData {
  totalMatches: number;
  wins: number;
  losses: number;
  winRate: string;
  tournamentResults: TournamentResult[];
}

interface ParticipantStatisticsProps {
  participantId: number;
}

const ParticipantStatistics: React.FC<ParticipantStatisticsProps> = ({ participantId }) => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);

  useEffect(() => {
    fetchStatistics();
  }, [participantId]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<{ success: boolean; data: StatisticsData }>(
        API_ENDPOINTS.PARTICIPANTS.STATISTICS(participantId)
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      } else {
        message.error('Не удалось загрузить статистику');
      }
    } catch (error: any) {
      logger.error('Error fetching participant statistics:', error);
      message.error('Ошибка при загрузке статистики');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Турнир',
      dataIndex: 'tournamentName',
      key: 'tournamentName',
    },
    {
      title: 'Дата',
      dataIndex: 'tournamentDate',
      key: 'tournamentDate',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Категория',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (categoryName: string | null) => categoryName || '-',
    },
    {
      title: 'Место',
      dataIndex: 'position',
      key: 'position',
      render: (position: number | null) => {
        if (position === null) return '-';
        if (position <= 3) return position;
        return '-';
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!statistics) {
    return (
      <Card>
        <p>Статистика недоступна</p>
      </Card>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Всего боев"
              value={statistics.totalMatches}
              prefix={<TrophyTwoTone twoToneColor="#52c41a" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Побед"
              value={statistics.wins}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Процент побед"
              value={statistics.winRate}
              suffix="%"
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Title level={4}>Результаты в турнирах</Title>
        <Table
          columns={columns}
          dataSource={statistics.tournamentResults}
          rowKey="tournamentId"
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default ParticipantStatistics;

